import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MultiColumnList, Select, Row, Col } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import EmptyData from '../../../../common/emptyData/EmptyData';
import useFieldLabelsAPI from '../../../../../hooks/useFieldLabelsAPI';
import { buildAnnotationMap, buildFormattingOptions, formatNumber, createFieldFormatter } from '../../../../../helpers/fieldValueFormatter';

function GenericPivotTable({
  resultSet,
  reportType,
  formatting,
  pivotRowDimension: savedRowDim,
  pivotColDimension: savedColDim,
  fieldAliasMap = {},
  linkHiddenFields = [],
}) {
  const intl = useIntl();
  const annotationMap = useMemo(() => buildAnnotationMap(resultSet), [resultSet]);
  const fmtOptions = useMemo(() => buildFormattingOptions(formatting, intl), [formatting, intl]);
  const { labelsMap: dimLabels } = useFieldLabelsAPI({ fieldType: 'dimension' });
  const { labelsMap: measLabels } = useFieldLabelsAPI({ fieldType: 'measure' });
  const labelsMap = { ...dimLabels, ...measLabels };

  const resolveLabel = (key) => {
    const parts = key.split('_');
    for (let i = 1; i < parts.length; i++) {
      const candidate = parts.slice(0, i).join('_') + '.' + parts.slice(i).join('_');
      const alias = fieldAliasMap?.[candidate];
      if (alias && labelsMap?.[alias]) return labelsMap[alias];
      if (labelsMap?.[candidate]) return labelsMap[candidate];
    }
    if (labelsMap?.[key]) return labelsMap[key];
    return key.split('_').pop().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Detect available dimensions from data
  const { availableDimensions, measureKeys: detectedMeasures } = useMemo(() => {
    if (!resultSet?.loadResponses?.length) return { availableDimensions: [], measureKeys: [] };
    const data = resultSet.loadResponses[0]?.data || [];
    if (!data.length) return { availableDimensions: [], measureKeys: [] };
    const allKeys = Object.keys(data[0]);
    const mKeys = allKeys.filter(k => {
      const val = data[0][k];
      return val !== null && val !== undefined && !isNaN(Number(val)) && String(val).trim() !== '';
    });
    return { availableDimensions: allKeys.filter(k => !mKeys.includes(k) && !linkHiddenFields.includes(k)), measureKeys: mKeys };
  }, [resultSet, linkHiddenFields]);

  // Runtime selections — default to saved values or first/second dimension
  const [activeRowDim, setActiveRowDim] = useState(savedRowDim || availableDimensions[0] || '');
  const [activeColDim, setActiveColDim] = useState(savedColDim || availableDimensions[1] || '');

  const effectiveRowDim = activeRowDim || availableDimensions[0] || '';
  const effectiveColDim = activeColDim || availableDimensions[1] || '';

  const pivotData = useMemo(() => {
    if (!resultSet?.loadResponses?.length) return null;
    const data = resultSet.loadResponses[0]?.data || [];
    if (!data.length || !effectiveRowDim || !effectiveColDim) return null;

    const measureKey = detectedMeasures[0] || '';
    const rowDimNorm = effectiveRowDim.replace('.', '_');
    const colDimNorm = effectiveColDim.replace('.', '_');

    // Translated row header
    const rowDimLabel = resolveLabel(rowDimNorm);

    // Unique column values
    const colValues = [...new Set(data.map(r => r[effectiveColDim]))].filter(v => v != null).sort();
    const colKeyMap = {};
    colValues.forEach((cv, i) => { colKeyMap[cv] = `__col${i}`; });

    // Build pivot map
    const pivotMap = {};
    data.forEach(row => {
      const rVal = row[effectiveRowDim] != null ? String(row[effectiveRowDim]) : '—';
      const cVal = row[effectiveColDim];
      const mVal = row[measureKey];
      if (!pivotMap[rVal]) pivotMap[rVal] = {};
      if (cVal != null) pivotMap[rVal][cVal] = mVal;
    });

    // Format measure value using fieldValueFormatter
    const formatVal = (val) => {
      if (val == null || val === '') return '0';
      if (!isNaN(Number(val))) return formatNumber(val, fmtOptions);
      return String(val);
    };

    // Translate row/col values
    const rowRefFormatter = createFieldFormatter(rowDimNorm, annotationMap, { intl });
    const colRefFormatter = createFieldFormatter(colDimNorm, annotationMap, { intl });

    const visibleColumns = ['_rowLabel', ...colValues.map(cv => colKeyMap[cv])];

    const columnMapping = { _rowLabel: rowDimLabel };
    colValues.forEach(cv => {
      const fakeItem = { [colDimNorm]: cv };
      columnMapping[colKeyMap[cv]] = (colRefFormatter ? colRefFormatter(fakeItem) : null) || String(cv);
    });

    const contentData = Object.entries(pivotMap).map(([rVal, colMap]) => {
      const fakeItem = { [rowDimNorm]: rVal };
      const translatedRowLabel = (rowRefFormatter ? rowRefFormatter(fakeItem) : null) || rVal;
      const row = { _rowLabel: translatedRowLabel };
      colValues.forEach(cv => { row[colKeyMap[cv]] = formatVal(colMap[cv]); });
      return row;
    });

    return { visibleColumns, columnMapping, contentData };
  }, [resultSet, effectiveRowDim, effectiveColDim, formatting, labelsMap, fieldAliasMap, intl, detectedMeasures, fmtOptions]);

  if (!availableDimensions.length) return <EmptyData />;

  const dimOptions = availableDimensions.map(dim => {
    const dimNorm = dim.replace('.', '_');
    return { value: dim, label: resolveLabel(dimNorm) };
  });

  return (
    <div>
      {/* Runtime dimension selectors */}
      <div style={{ padding: '0.5rem 1rem 0' }}>
        <Row>
          <Col xs={6}>
            <Select
              label={
                <FormattedMessage
                  id="ui-reports.visualization.pivotRowDimension.label"
                  defaultMessage="Row dimension"
                />
              }
              value={effectiveRowDim}
              dataOptions={dimOptions}
              onChange={e => setActiveRowDim(e.target.value)}
              marginBottom0
            />
          </Col>
          <Col xs={6}>
            <Select
              label={
                <FormattedMessage
                  id="ui-reports.visualization.pivotColDimension.label"
                  defaultMessage="Column dimension"
                />
              }
              value={effectiveColDim}
              dataOptions={dimOptions}
              onChange={e => setActiveColDim(e.target.value)}
              marginBottom0
            />
          </Col>
        </Row>
      </div>

      {/* Pivot table */}
      {!pivotData || !pivotData.contentData?.length
        ? <EmptyData />
        : (
          <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
            <MultiColumnList
              id="generic-pivot-table"
              interactive={false}
              visibleColumns={pivotData.visibleColumns}
              contentData={pivotData.contentData}
              columnMapping={pivotData.columnMapping}
              maxHeight={600}
              hasMargin
            />
          </div>
        )
      }
    </div>
  );
}

GenericPivotTable.propTypes = {
  resultSet:         PropTypes.object.isRequired,
  reportType:        PropTypes.string,
  formatting:        PropTypes.object,
  pivotRowDimension: PropTypes.string,
  pivotColDimension: PropTypes.string,
  fieldAliasMap:     PropTypes.object,
  linkHiddenFields:  PropTypes.arrayOf(PropTypes.string),
};

export default GenericPivotTable;
